import { useCallback, useState } from 'react'
import { useParams } from 'react-router'
import { ModalOpenedSetTy } from '../../../../../../../../baseComponents/Modal/types'
import { ModalCreateEditBase } from '../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { RowData } from '../../../../../../../../baseComponents/TwinTable/types'
import VirtualTableListing from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing'
import { ColumnTableSchema } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import CustomSelect from '../../../../../../../../forms/CustomSelect'
import { FormRender } from '../../../../../../../../forms/FormRenderer/types'
import { RectangularInput } from '../../../../../../../../forms/Input'
import { BeforeSubmitHandler, OnSubmit } from '../../../../../../../../forms/TwinForm/types'
import { getLang } from '../../../../../../../../utils/globals'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { getActiveLangs } from '../../../../../../../../utils/reducers/getters'
import { getVariantPreparedFromProducts } from '../../functions'
import { ProductVariantPrepared, ProductVariantsPrepared } from '../../types'
import { ProductExtendedTyShort } from '../../../../../../types'

interface EditVariantsGeneralProps extends EditVariantsGeneralLogicProps {
    allRowData?: RowData
}

export const EditVariantsGeneral: React.FC<EditVariantsGeneralProps> = ({ onSubmit, allRowData, items, setOpened}) => {
    const { id } = useParams()
    const { data, changeData, setLang, changeValCurrentLang, lang, beforeSubmit, handleOnSubmit } = useEditVariantsGeneralLogic({ items, onSubmit, setOpened })
    const { t } = useTwinTranslation()
    const fields: FormRender = [
        {
            cols: 1,
            title: t('updateVariantsGeneralFields', 'Editar campos generales de las variantes'),
            elements: [
                {
                    component: 'InputHidden',
                    name: 'ProductId',
                    value: id
                }
            ]
        }
    ]
    const columns: ColumnTableSchema<ProductVariantPrepared> = [
        {
            id: 'name' + lang,
            dataKey: 'name',
            label: t('name', 'Nombre'),
            customRender: (parameterValue, allRowData) => {
                return <RectangularInput value={parameterValue[lang] ?? ''} onChange={(value) => changeValCurrentLang(value, allRowData.rowIndex)}></RectangularInput>
            },
        },
        {
            id: 'reference'+lang,
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            customRender: (parameterValue, allRowData) => <RectangularInput value={parameterValue ?? ''} required={true} onChange={(value) => changeData('reference', value, allRowData.rowIndex)}></RectangularInput>
        },
        {
            id: 'code_bar'+lang,
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            customRender: (parameterValue, allRowData) => <RectangularInput value={parameterValue ?? ''} onChange={(value) => changeData('code_bar', value, allRowData.rowIndex)}></RectangularInput>
        }
    ]
    return (
        <ModalCreateEditBase url={'/api/app/product/updateSubProducts'} onSubmit={handleOnSubmit} translations={{ title: t('updateVariants', 'Editar variantes'), button: t('save', 'Guardar') }} haveButtonPermissions={true} fields={fields} setOpened={() => { }} beforeSubmit={beforeSubmit} >
            <CustomSelect value={lang} items={getActiveLangs()} label={t('lang', 'Idioma')} onChange={setLang}></CustomSelect>
            <div className='flex-auto'>
                <VirtualTableListing tableData={data} columns={columns} name='EditVariantsGeneral' />
            </div>
        </ModalCreateEditBase>
    )
}

interface EditVariantsGeneralLogicProps {
    items: ProductExtendedTyShort[]
    onSubmit: OnSubmit
    setOpened: ModalOpenedSetTy
}

const useEditVariantsGeneralLogic = ({ items, onSubmit, setOpened }: EditVariantsGeneralLogicProps) => {
    const [lang, setLang] = useState(getLang())
    const [data, setData] = useState<ProductVariantsPrepared>(() => {
        return getVariantPreparedFromProducts(items)
    })

    const changeData = useCallback(<K extends keyof ProductVariantPrepared>(key: K, value: ProductVariantPrepared[K], rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const copy = JSON.parse(JSON.stringify(oldData))
            copy[rowIndex][key] = value
            return ([...copy])
        })
    }, [setData])

    const changeValCurrentLang = useCallback((value: string, rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const copy = JSON.parse(JSON.stringify(oldData))
            if (!copy[rowIndex].name) {
                copy[rowIndex].name = {}
            }
            copy[rowIndex].name[lang] = value
            return ([...copy])
        })
    }, [setData, lang])

    const beforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        vals.combinations = data as any
        return true
    }, [data])

    const handleOnSubmit: OnSubmit = useCallback((rest, vals) => {
        onSubmit(rest, vals)
        setOpened(null)
    }, [onSubmit, setOpened]) 

    return { beforeSubmit, data, changeData, changeValCurrentLang, lang, setLang, handleOnSubmit }
}